
import {useEffect, useState, useRef, useCallback, memo} from 'react';
import {DragOption} from '@/interface';
import sdk from '@/sdk';
import * as store from '@/store';
import eventBus, {SIDEBAR_WIDTH_CHANGE} from '@/eventBus';
import DragLine, {DragLineOptions} from '@/components/DragLine';
import * as storageUtils from '@/utils/storageUtils';
import {
    SidebarWidthName,
    EDITOR_MINWIDTH,
} from '@/Constant';
import classNames from 'classnames';
import styles from './index.module.scss';
import Sidebar from '@/domains/space/Repository/SidebarNew';


const minSidebarOpenWidth = 240;
const maxSidebarOpenWidth = 480;
const defaultSidebarOpenWidth = 240;
const defaultSidebarCloseWidth = 20;

function getDefaultSidebarWidth(isKuShareMode?: string) {
    let storageSidebarWidth = storageUtils.get(SidebarWidthName);
    if (!storageSidebarWidth || storageSidebarWidth.open === undefined) {
        storageSidebarWidth = {width: defaultSidebarOpenWidth, open: !isKuShareMode};
    }
    return storageSidebarWidth;
}

interface SidebarContainerProps {
    className?: string;
    isPersonalKb: boolean;
    isRecycleBin: boolean;
    isSetting: boolean;
    isHomeTag: boolean;
    isMemberManage: boolean;
    docGuid?: string;
    repository: Repository | null;
    sidebarDragOption?: DragOption;
    isKuShareMode?: string;
    curDepartment?: Department;
    treeData?: any;
}


function SidebarContainer(props: SidebarContainerProps) {
    const {
        className,
        isPersonalKb,
        isRecycleBin,
        isSetting,
        isHomeTag,
        docGuid,
        repository,
        isMemberManage,
        sidebarDragOption,
        isKuShareMode,
        curDepartment,
        treeData,
    } = props;
    let originalSidebarWidth = 0;
    const defaultWidth = getDefaultSidebarWidth(isKuShareMode);
    const dragLineTargetElement = useRef<HTMLDivElement | null>(null);
    const [haveSidebarAnimation, setHaveSidebarAnimation] = useState(true);

    const defaultSidebarInfo = {
        sidebarWidth: defaultWidth.width,
        lastSidebarWidth: 0,
        sidebarOpen: defaultWidth.open,
    };

    useEffect(() => {
        store.setSidebarDragOption(defaultSidebarInfo);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 设置页面获取风险文档数量的接口，在toc里用不上
    // useEffect(() => {
    //     async function getAuthRiskCount() {
    //         try {
    //             if (!repository?.repositoryGuid) {
    //                 return;
    //             }
    //             const {
    //                 riskCount,
    //             } = await sdk.as.giveRepositoryAuthRiskNum(repository?.repositoryGuid);
    //             store.setRepoPermissionRiskCount(riskCount);
    //         } catch (e) {
    //             console.log(e);
    //         }
    //     }
    //     getAuthRiskCount();
    // }, [repository?.repositoryGuid]);

    const sidebarDragOptions = sidebarDragOption || defaultSidebarInfo;
    const timeoutId = useRef<null | NodeJS.Timeout>(null);
    const sidebarChange = useCallback(
        (type: string, width?: number) => {
            let sideWidth = 0;
            let isClick = true;
            switch (type) {
                case 'close':
                    sideWidth = defaultSidebarCloseWidth;
                    break;
                case 'move':
                    sideWidth = width || sidebarDragOptions.sidebarWidth;
                    isClick = false;
                    break;
                default:
                    sideWidth = sidebarDragOptions.sidebarWidth;
                    break;
            }
            timeoutId.current && window.clearTimeout && window.clearTimeout(timeoutId.current);
            // 数据后防抖
            // 防止拖拽过程中高频率修改stor数据导致不必要的dom刷新
            timeoutId.current = setTimeout(() => {
                timeoutId.current = null;
                store.setSidebarWidthChangeInfo({width: sideWidth, isClick});
            }, 200);

            if (type === 'close') {
                // transition 动画结束后再触发一次，此时dom才更新
                setTimeout(() => {
                    eventBus.emit(SIDEBAR_WIDTH_CHANGE, {width: sideWidth});
                }, 500);
            } else {
                eventBus.emit(SIDEBAR_WIDTH_CHANGE, {width: sideWidth});
            }
        },
        [sidebarDragOptions.sidebarWidth]
    );

    function handleDragWidthStart() {
        document.body.classList.add('no-select');
        const dragLineTarget = dragLineTargetElement.current;
        if (dragLineTarget) {
            originalSidebarWidth = dragLineTarget.clientWidth;
            setHaveSidebarAnimation(false);
            const options: DragLineOptions = {
                maxLeft: maxSidebarOpenWidth,
                minLeft: minSidebarOpenWidth,
            };
            return options;
        }
        return null;
    }

    function handleDragWidthEnd() {
        document.body.classList.remove('no-select');
        const {lastSidebarWidth, sidebarOpen} = sidebarDragOptions;

        storageUtils.set(SidebarWidthName, {width: originalSidebarWidth, open: sidebarOpen});

        store.setSidebarDragOption({
            lastSidebarWidth,
            sidebarWidth: originalSidebarWidth,
            sidebarOpen: sidebarOpen,
        });
        setHaveSidebarAnimation(true);
    }

    function handleDragWidthMove(moveX: number) {
        const dragLineTarget = dragLineTargetElement.current;
        if (dragLineTarget) {
            const curWidth = dragLineTarget.clientWidth + moveX;
            dragLineTarget.style.width = `${curWidth}px`;
            originalSidebarWidth = curWidth;
            sidebarChange('move', curWidth);
        }
    }

    const handleCollapse = useCallback(() => {
        // 关闭侧边栏
        const {lastSidebarWidth, sidebarWidth} = sidebarDragOptions;
        storageUtils.set(SidebarWidthName, {width: sidebarWidth, open: false});
        store.setSidebarDragOption({
            lastSidebarWidth,
            sidebarWidth,
            sidebarOpen: false,
        });
        sidebarChange('close');
    }, [sidebarChange, sidebarDragOptions]);

    function handleExpand() {
        // 展开侧边栏
        const {lastSidebarWidth, sidebarWidth} = sidebarDragOptions;
        storageUtils.set(SidebarWidthName, {width: sidebarWidth, open: true});
        store.setSidebarDragOption({
            lastSidebarWidth,
            sidebarWidth,
            sidebarOpen: true,
        });

        sidebarChange('open');
    }

    useEffect(() => {
        let oldWindowWidth = document.body.offsetWidth;
        function windowSelf() {
            const {lastSidebarWidth, sidebarWidth, sidebarOpen} = sidebarDragOptions;
            const editorWidth = oldWindowWidth - sidebarWidth;
            const curWindowWidth = document.body.offsetWidth;
            const isShrink = oldWindowWidth > curWindowWidth;
            if (isShrink) {
                if (sidebarOpen && sidebarWidth <= minSidebarOpenWidth && editorWidth < EDITOR_MINWIDTH) {
                    handleCollapse();
                } else if (sidebarOpen && sidebarWidth > minSidebarOpenWidth) {
                    let sidebarZoomWidth = sidebarWidth - (oldWindowWidth - curWindowWidth);
                    sidebarZoomWidth = sidebarZoomWidth <= minSidebarOpenWidth ? minSidebarOpenWidth : sidebarZoomWidth;
                    storageUtils.set(SidebarWidthName, {width: sidebarZoomWidth});
                    store.setSidebarDragOption({
                        lastSidebarWidth,
                        sidebarWidth: sidebarZoomWidth,
                        sidebarOpen: true,
                    });
                }
            }
            oldWindowWidth = curWindowWidth;
        }
        windowSelf();
        window.addEventListener('resize', windowSelf);
        return () => {
            window.removeEventListener('resize', windowSelf);
        };
    }, [handleCollapse, sidebarDragOptions]);


    // 对外场景非文档页，目录树数据尚未加载完成前，侧边栏保持收起状态。 数据加载完成后，按厂内逻辑展示
    let sideBarWidth = defaultSidebarCloseWidth;
    const isTreeLoaded = store.getTreeLoaded();
    // 1、文档页，直接按照上次记录的宽度展示
    if (docGuid) {
        sideBarWidth = sidebarDragOptions.sidebarOpen ? defaultSidebarOpenWidth : defaultSidebarCloseWidth;
    // 2、非文档页，需要等待目录树数据加载完成&当前存在可查看的目录，才能按照上次记录的宽度展示
    } else if (isTreeLoaded && treeData.length > 0) {
        sideBarWidth = sidebarDragOptions.sidebarOpen ? defaultSidebarOpenWidth : defaultSidebarCloseWidth;
    }

    return (
        <div
            id="sideBar"
            className={classNames(
                styles.sidebarContainer,
                haveSidebarAnimation ? styles.sidebarAnimation : styles.sidebarAnimationDrag,
                className
            )}
            style={{
                transition: 'width 40ms linear',
                width: sideBarWidth,
            }}
            ref={dragLineTargetElement}
        >
            <div
                className={classNames(
                    styles.sidebar,
                    !sidebarDragOptions.sidebarOpen && styles.hide
                )}
            >
                <Sidebar
                    isPersonalKb={isPersonalKb}
                    isRecycleBin={isRecycleBin}
                    isSetting={isSetting}
                    isHomeTag={isHomeTag}
                    isMemberManage={isMemberManage}
                    repository={repository}
                    docGuid={docGuid}
                    curDepartment={curDepartment}
                />
            </div>
            {
                // 非文档页，目录树数据加载完成且当前目录存在，才展示拖拽线
                (docGuid || (isTreeLoaded && treeData.length > 0)) && (
                    <DragLine
                        sidebarDragOption={sidebarDragOptions}
                        onCollapse={handleCollapse}
                        onExpand={handleExpand}
                        onDragWidthStart={handleDragWidthStart}
                        onDragWidthEnd={handleDragWidthEnd}
                        onDragWithMove={handleDragWidthMove}
                    />
                )
            }
        </div>
    );
}

export const SidebarContainerImpl = memo(store.connect([
    store.KEYS.SIDE_BAR_DRAG_OPTION,
    store.KEYS.IS_KU_SHARE_MODE,
    store.KEYS.TREE_DATA,
])(SidebarContainer));


// export default function SidebarWrap() {
//     if (!repository) {
//         return null;
//     }
//     return (
//         <Profiler id={PROFILER_KEY.sideBar} onRender={perfLogger.compRender.onRender}>
//             <SidebarContainerImpl
//                 isPersonalKb={isPersonalKb}
//                 isRecycleBin={isRecycleBin}
//                 isSetting={isSetting}
//                 isHomeTag={isHomeTag}
//                 isMemberManage={isMemberManage}
//                 docGuid={params.docGuid}
//                 repository={repository}
//                 curDepartment={curDepartment}
//             />
//         </Profiler>
//     );
// }
